// R110 #3 (Jesse: "make the EOD report searchable/filterable, and not all
// listed at once — maybe just the last week's reports, and collapsed").
// Pure filtering / grouping / display helpers for Daily Ops → EOD Reports.
// No DOM, no I/O. ESM (like woSearch.mjs) so the Vite client imports it
// straight from EodPage.ts and `node lib/eodFeed.test.mjs` tests the SAME code
// the page runs. The server is untouched: GET /eod?days= keeps its 14 default;
// the page simply asks for the whole retention window once and filters here.

export const RETENTION_DAYS = 90;          // server keeps 90 days (EOD_KEEP_DAYS) — "All" = that whole window
export const WINDOWS = [7, 14, 30, RETENTION_DAYS];
export const DEFAULT_WINDOW = 7;           // "just the last week's reports"
export const COLLAPSED_CHARS = 110;

const lc = (s) => String(s == null ? '' : s).trim().toLowerCase();

// "Lorrie  pool heater" → ['lorrie', 'pool', 'heater'] (lowercase, deduped).
export function searchWords(q) {
  const out = [];
  for (const w of lc(q).split(/\s+/)) if (w && !out.includes(w)) out.push(w);
  return out;
}

// The text a search runs against: person · department · summary · blockers ·
// tomorrow — everything a reader can see on the card, nothing hidden.
export function reportText(r) {
  return [r.person, r.department, r.summary, r.blockers, r.tomorrow].map(lc).join('\n');
}

// Every word must appear somewhere in the report (AND, case-insensitive
// substring) — "mark heater" finds Mark's report about the heater, not every
// report by Mark plus every heater.
export function matchesSearch(r, words) {
  if (!words || !words.length) return true;
  const text = reportText(r);
  return words.every(w => text.includes(lc(w)));
}

export function addDays(iso, n) {
  const d = new Date(String(iso) + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Same rule the server applies to ?days= (date >= today − days), so "All (90)"
// shows exactly what GET /eod?days=90 returned — nothing silently hidden.
export function inWindow(r, today, days) {
  if (!today) return true;
  return String((r && r.date) || '') >= addDays(today, -days);
}

// "Mine" = the signed-in person's reports. Email is the durable key (R48 —
// display names drift when a profile gains a full_name); the name match keeps
// legacy rows filed before person_email existed.
export function isMine(r, me) {
  const email = lc(me && me.email);
  const name = lc(me && me.name);
  if (email && lc(r.person_email) === email) return true;
  return !!name && lc(r.person) === name;
}

// Typing searches the whole retention window: a 7-day window would make
// "did Mark mention the pool heater?" come back empty for no visible reason.
export function effectiveDays(state) {
  return searchWords(state.q).length ? RETENTION_DAYS : (Number(state.days) || DEFAULT_WINDOW);
}

export function hasActiveFilters(state) {
  return !!(searchWords(state.q).length
    || (state.dept && state.dept !== 'all')
    || state.mine
    || (Number(state.days) || DEFAULT_WINDOW) !== DEFAULT_WINDOW);
}

// state = { q, dept ('all' | department), mine, days }
// ctx   = { today (YYYY-MM-DD business day from the server), me: { name, email } }
export function filterReports(reports, state, ctx) {
  const words = searchWords(state.q);
  const days = effectiveDays(state);
  const dept = lc(!state.dept || state.dept === 'all' ? '' : state.dept);
  const today = (ctx && ctx.today) || '';
  const me = (ctx && ctx.me) || {};
  const keep = (ctx && ctx.keep) || null;   // the report being edited stays on screen whatever the filters say
  const rows = (reports || []).filter(r => r && ((keep && r.id === keep) || (
    inWindow(r, today, days)
    && (!dept || lc(r.department) === dept)
    && (!state.mine || isMine(r, me))
    && matchesSearch(r, words))));
  return { rows, words, days, searching: words.length > 0 };
}

// Department chips: the known list in its fixed order, only those present in
// the loaded reports, then any legacy spelling that isn't in the list (so no
// report is unreachable through the chips).
export function departmentsPresent(reports, known) {
  const present = new Map();   // lowercase → first-seen spelling
  for (const r of reports || []) {
    const d = String((r && r.department) || '').trim();
    if (d && !present.has(d.toLowerCase())) present.set(d.toLowerCase(), d);
  }
  const out = [];
  for (const k of known || []) {
    const key = String(k).toLowerCase();
    if (present.has(key)) { out.push(k); present.delete(key); }
  }
  for (const v of present.values()) out.push(v);
  return out;
}

// Keeps the server's newest-first order (by day, then by time within a day).
export function groupByDate(rows) {
  const by = new Map();
  for (const r of rows || []) {
    if (!by.has(r.date)) by.set(r.date, []);
    by.get(r.date).push(r);
  }
  return [...by.entries()].map(([date, list]) => ({ date, rows: list }));
}

export function fmtDay(iso) {
  const d = new Date(String(iso) + 'T00:00:00Z');
  if (isNaN(d.getTime())) return String(iso || '');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// "Wed, Oct 7 · 3 reports · Lorrie, Mark, Ana" — the names mean a closed day
// still tells you who filed without opening it. Parts are returned separately
// so the page can escape `who` and style the pieces.
export function dayHeader(group, today, maxNames = 4) {
  const names = [];
  for (const r of group.rows) {
    const p = String((r && r.person) || '').trim() || 'unknown';
    if (!names.some(n => n.toLowerCase() === p.toLowerCase())) names.push(p);
  }
  const who = names.length > maxNames
    ? `${names.slice(0, maxNames).join(', ')} +${names.length - maxNames}`
    : names.join(', ');
  const n = group.rows.length;
  return { label: group.date === today ? 'Today' : fmtDay(group.date), count: `${n} report${n === 1 ? '' : 's'}`, who };
}

// The one-line collapsed card: first ~110 chars of the summary on one line,
// cut at a word boundary, trailing punctuation dropped before the ellipsis.
export function collapsedSummary(summary, max = COLLAPSED_CHARS) {
  const s = String(summary == null ? '' : summary).replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max + 1);
  const sp = cut.lastIndexOf(' ');
  const head = sp > max * 0.6 ? cut.slice(0, sp) : s.slice(0, max);
  return head.replace(/[\s,;:.\-–—]+$/, '') + '…';
}

export function resultLine(n, days, searching) {
  const reports = `${n} report${n === 1 ? '' : 's'}`;
  if (searching) return `${reports} · searching all ${RETENTION_DAYS} days`;
  return `${reports} · ${days === RETENTION_DAYS ? `all ${RETENTION_DAYS}` : days} days`;
}

// R113 #6 (de-clutter): the toolbar's muted count no longer restates the
// window — the active 7d/14d/30d/90d tab already says it. While a search is
// on, the window tabs are hidden, so the count carries the "all 90 days" fact.
export function resultText(n, searching) {
  if (searching) return `${n} match${n === 1 ? '' : 'es'} · all ${RETENTION_DAYS} days`;
  return `${n} report${n === 1 ? '' : 's'}`;
}

// R113 #6: the composer collapses to "✓ You filed today at h:mm · Add another"
// once the signed-in person has a report dated today. Reports arrive newest
// first, so the first hit is the latest one filed. null when nothing / no today.
export function filedToday(reports, today, me) {
  if (!today) return null;
  for (const r of reports || []) {
    if (r && String(r.date || '') === String(today) && isMine(r, me)) return r;
  }
  return null;
}

// Day groups: today open, earlier days closed, the user's own toggle wins, and
// an active search opens everything (the matches are what they asked for).
export function dayOpen(date, { today, searching, overrides }) {
  if (searching) return true;
  if (overrides && overrides.has(date)) return !!overrides.get(date);
  return !!today && date === today;
}

// A report card is open when the user expanded it, when it's being edited (an
// inline edit must never collapse on redraw), or when a search is active.
export function reportOpen(id, { open, editing, searching }) {
  return !!searching || (!!editing && editing === id) || !!(open && open.has(id));
}

// Escape FIRST, then wrap matches in <mark>: ranges are found on the raw
// string, every segment is escaped on its own, so a "<" in a report can never
// become markup and a match can never split an entity.
export function highlight(raw, words, esc, cls = 'eod-hl') {
  const s = String(raw == null ? '' : raw);
  const lower = s.toLowerCase();
  // Some scripts change length under toLowerCase (İ → i̇) — indices would drift; skip marks rather than mis-wrap.
  if (!words || !words.length || lower.length !== s.length) return esc(s);
  const rng = [];
  for (const raw of words) {
    const w = lc(raw);
    if (!w) continue;
    let i = 0;
    while ((i = lower.indexOf(w, i)) >= 0) { rng.push([i, i + w.length]); i += w.length; }
  }
  if (!rng.length) return esc(s);
  rng.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of rng) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]); else merged.push([r[0], r[1]]);
  }
  let out = '', pos = 0;
  for (const [a, b] of merged) {
    out += esc(s.slice(pos, a)) + `<mark class="${cls}">` + esc(s.slice(a, b)) + '</mark>';
    pos = b;
  }
  return out + esc(s.slice(pos));
}

// R115 fix (eod 6): while "Generate now" polls GET /eod/ai every 3 s, the
// feed is redrawn ONLY when something it shows changed — today's AI report
// landed / changed id, its narrative status or source flipped, or the
// "writing now…" state started / stopped — and never while the user is busy
// in the feed (an inline edit, an open ⋯ menu, a focused field there). A
// redraw that is skipped loses nothing: the data is already in memory and the
// next redraw (Save / Cancel / a chip) shows it; the poll ends with one
// catch-up redraw when the user is idle.
export function aiPollSig(reports, today, status) {
  const m = (reports || []).find(x => x && x.date === today) || null;
  // "writing tonight's report now…" is only drawn on today's stub, i.e. when
  // there is no card for today yet
  const run = !m && status && status.running ? String(status.running.date || 'running') : '';
  return JSON.stringify([m ? String(m.id || '') : '', m && m.ai ? String(m.ai.status || '') : '', m ? String(m.narrative_source || '') : '', run]);
}
export function aiPollRedraw({ drawn, now, busy, onScreen }) {
  return now !== drawn && !busy && !!onScreen;
}

// R115 fix-2 (D2): the office note bar above the composer. payload = GET
// /eod/ai/notes — {for_date, ran_tonight, notes (what the NEXT report reads),
// included (what tonight's report already used)}. Before tonight's run the
// bar is "Tonight's AI report" and a note carried from yesterday says so;
// after it, "Tomorrow night's AI report" lists only the notes filed since,
// and tonight's are a separate group whose × says the truth (deleting one
// does not change a report that already ran). statusRan is the fallback when
// the payload has no ran_tonight (an older server).
export function aiNoteBar(payload, { statusRan = false } = {}) {
  const p = payload || {};
  const ran = typeof p.ran_tonight === 'boolean' ? p.ran_tonight : !!statusRan;
  const which = ran ? "tomorrow night's" : "tonight's";
  const next = Array.isArray(p.notes) ? p.notes : [];
  const used = ran && Array.isArray(p.included) ? p.included : [];
  const groups = [{
    key: 'next', label: '',
    notes: next.map(n => ({ ...n, tag: !ran && n.late ? "filed after last night's report" : '' })),
    confirm: `Remove this note from ${which} report?`,
  }];
  if (used.length) groups.push({
    key: 'included', label: `Already in tonight's report · ${used.length}`,
    notes: used.map(n => ({ ...n, tag: '' })),
    confirm: "Delete this note? Tonight's report already used it — deleting it does not change that report.",
  });
  return { ran, which, heading: ran ? "Tomorrow night's AI report" : "Tonight's AI report", count: next.length, groups };
}
// The × confirm for one note id (the group it is in), or null when unknown.
export function aiNoteConfirm(bar, id) {
  for (const g of (bar && bar.groups) || []) if (g.notes.some(n => n && n.id === id)) return g.confirm;
  return null;
}
